import type { ConditionOperator, TriggerType } from '../types/automationRule'

/** Matches `Appointment.type` in `types/appointment.ts` (kind of visit). */
const APPOINTMENT_VISIT_TYPE_SUGGESTIONS: string[] = [
  'consultation',
  'follow-up',
  'checkup',
  'procedure',
  'emergency',
  'other',
]

/** Matches `Appointment.status` in `types/appointment.ts`. */
const APPOINTMENT_STATUS_SUGGESTIONS: string[] = [
  'scheduled',
  'arrived',
  'in-progress',
  'completed',
  'cancelled',
  'dna',
]

export interface ConditionFieldOption {
  /** Property name on the trigger payload evaluated by the automation Lambda */
  field: string
  /** Short description shown in the typeahead list */
  label: string
  /** Typical values for the condition (typeahead / datalist); users may still type other values */
  valueSuggestions?: string[]
}

export const AUTOMATION_CONDITION_FIELDS_BY_TRIGGER: Record<TriggerType, ConditionFieldOption[]> = {
  'lab-result-created': [
    {
      field: 'reviewStatus',
      label: 'Review status',
      valueSuggestions: ['in-review', 'reviewed'],
    },
    { field: 'testName', label: 'Test name (contains match)' },
    { field: 'resultValue', label: 'Result value (free text / number)' },
    { field: 'isCritical', label: 'Critical flag', valueSuggestions: ['true', 'false'] },
  ],
  'lab-result-updated': [
    {
      field: 'reviewStatus',
      label: 'Review status',
      valueSuggestions: ['in-review', 'reviewed'],
    },
    { field: 'testName', label: 'Test name (contains match)' },
    { field: 'isCritical', label: 'Critical flag', valueSuggestions: ['true', 'false'] },
  ],
  'medication-review-due': [
    {
      field: 'reviewDueDate',
      label: 'Review due date',
      valueSuggestions: ['now'],
    },
    { field: 'status', label: 'Medication status' },
    { field: 'drugName', label: 'Drug name (contains)' },
  ],
  'appointment-scheduled': [
    {
      field: 'status',
      label: 'Appointment status',
      valueSuggestions: [...APPOINTMENT_STATUS_SUGGESTIONS],
    },
    {
      field: 'type',
      label: 'Visit type',
      valueSuggestions: [...APPOINTMENT_VISIT_TYPE_SUGGESTIONS],
    },
    { field: 'staffId', label: 'Staff member (assigned clinician)' },
    { field: 'providerId', label: 'Provider / resource id (if used in payload)' },
  ],
  'consultation-completed': [
    { field: 'status', label: 'Consultation status', valueSuggestions: ['completed', 'draft'] },
    { field: 'consultationType', label: 'Consultation type' },
  ],
  'vital-sign-abnormal': [
    { field: 'isAbnormal', label: 'Abnormal flag', valueSuggestions: ['true', 'false'] },
    {
      field: 'measurementType',
      label: 'Measurement type',
      valueSuggestions: ['bloodPressure', 'heartRate', 'temperature', 'oxygenSaturation'],
    },
    { field: 'severity', label: 'Severity' },
  ],
}

export function getConditionFieldOptions(trigger: TriggerType): ConditionFieldOption[] {
  return AUTOMATION_CONDITION_FIELDS_BY_TRIGGER[trigger] ?? []
}

export function getConditionValueSuggestions(
  trigger: TriggerType,
  fieldKey: string,
  operator: ConditionOperator
): string[] {
  const options = getConditionFieldOptions(trigger)
  const match = options.find((o) => o.field === fieldKey)
  const fromField = match?.valueSuggestions ? [...match.valueSuggestions] : []

  if (operator === 'lessThanOrEqual' || operator === 'greaterThanOrEqual') {
    if (!fromField.includes('now')) {
      fromField.push('now')
    }
  }

  // Only suggest generic booleans when this field has no specific value list (avoids true/false on visit type, etc.)
  if (
    (operator === 'equals' || operator === 'notEquals') &&
    fromField.length === 0
  ) {
    fromField.push('true', 'false')
  }

  return fromField
}
